/**
 * Design Master page. Per-company, paginated grid of reusable jewellery
 * designs, each carrying an auto-generated Design Number (RD-101+), one image,
 * an optional short note/name, and clickable chips — one per group of linked
 * order forms (green = active, red = all completed/on-hold/cancelled).
 *
 * Visible to ALL roles. Sales users see every design but only the chips for
 * their attributed customers (scoping enforced server-side); they cannot
 * create, edit or reassign.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus, Search, Upload, Trash2, Palette, ImageOff, Loader2, RefreshCw, Info,
  ChevronLeft, ChevronRight, Check, Link2, Unlink, ArrowRightLeft, Save,
} from 'lucide-react';
import { formatDate } from '@/lib/format';
import { extractError } from '@/lib/errors';
import { useDesignConfig } from '@/hooks/useDesignConfig';
import DesignChip from '@/components/designs/DesignChip';
import DesignPicker from '@/components/designs/DesignPicker';

const PAGE_SIZE = 25;

// Human label for a KB cap (e.g. 500 → "500 KB", 2048 → "2 MB").
function formatCapLabel(kb) {
  const n = Number(kb) || 0;
  if (n >= 1024) {
    const mb = n / 1024;
    return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
  }
  return `${n} KB`;
}

function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function DesignMaster() {
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell
      title="Design Master"
      subtitle="Reusable jewellery designs. Each design has a Design Number, one image, and links to the order forms that use it."
    >
      <CompanyGate>
        <DesignMasterContent companyId={effectiveCompanyId} />
      </CompanyGate>
    </AppShell>
  );
}

function DesignMasterContent({ companyId }) {
  const { isSalesRole } = useAuth();
  const canManage = !isSalesRole;
  const { enabled, nextPreview, refresh: refreshConfig } = useDesignConfig(companyId);

  const [list, setList] = useState([]);
  const [usesReference, setUsesReference] = useState(false);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // New Design dialog
  const [newOpen, setNewOpen] = useState(false);
  const [newImage, setNewImage] = useState('');
  const [newNote, setNewNote] = useState('');
  const [creating, setCreating] = useState(false);
  const newFileRef = useRef(null);

  // Detail dialog
  const [detail, setDetail] = useState(null); // { id, design_no }
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const replaceFileRef = useRef(null);
  const [replacing, setReplacing] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Reassign panel (inside detail)
  const [selOrders, setSelOrders] = useState(() => new Set());
  const [reassignBusy, setReassignBusy] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveDesignId, setMoveDesignId] = useState(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Super-Admin configured ERP attachment cap (KB; default 500). This is the
  // SAME cap the Order Forms jewellery-file uploader uses — configured in
  // Portal Access → Attachment Size Caps. Uploads larger than this are blocked.
  const [erpCapKb, setErpCapKb] = useState(500);
  useEffect(() => {
    api.get('/system/attachment-caps')
      .then(({ data }) => setErpCapKb(Number(data?.erp_kb) || 500))
      .catch(() => { /* keep the 500 KB default on failure */ });
  }, []);
  const maxBytes = (Number(erpCapKb) || 500) * 1024;
  const capLabel = formatCapLabel(erpCapKb);

  const load = useCallback(async (q, pg) => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/designs/', {
        params: { company_id: companyId, q: q || undefined, page: pg || 1, page_size: PAGE_SIZE },
      });
      setList(data?.designs || []);
      setUsesReference(!!data?.uses_reference_numbers);
      setTotal(typeof data?.total === 'number' ? data.total : (data?.designs || []).length);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load designs'));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { setPage(1); load(query, 1); refreshConfig(); /* eslint-disable-next-line */ }, [companyId]);

  // Debounced search — always resets to page 1.
  useEffect(() => {
    if (!companyId) return undefined;
    const t = setTimeout(() => { setPage(1); load(query, 1); }, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [query]);

  const goToPage = (pg) => {
    const next = Math.min(Math.max(1, pg), totalPages);
    setPage(next);
    load(query, next);
  };

  // ----- New Design -----
  const onNewFile = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    if (f.size > maxBytes) { toast.error(`“${f.name}” exceeds the ${capLabel} upload limit`); return; }
    try {
      const dataUrl = await fileToDataUrl(f);
      setNewImage(dataUrl);
    } catch {
      toast.error('Could not read that image');
    }
    if (newFileRef.current) newFileRef.current.value = '';
  };

  const createDesign = async () => {
    if (!newImage) { toast.error('Please choose a design image'); return; }
    setCreating(true);
    try {
      const { data } = await api.post('/designs/', { image_data_url: newImage, company_id: companyId, note: newNote || undefined });
      toast.success(`Design ${data.design_no} created`);
      setNewOpen(false);
      setNewImage('');
      setNewNote('');
      setPage(1);
      load(query, 1);
      refreshConfig();
    } catch (e) {
      toast.error(extractError(e, 'Failed to create design'));
    } finally {
      setCreating(false);
    }
  };

  // ----- Detail -----
  const refreshDetail = async (id) => {
    const { data } = await api.get(`/designs/${id}`);
    setDetailData(data);
    setNoteDraft(data?.note || '');
    setSelOrders(new Set());
    return data;
  };

  const openDetail = async (d) => {
    setDetail(d);
    setDetailData(null);
    setSelOrders(new Set());
    setDetailLoading(true);
    try {
      await refreshDetail(d.id);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load design'));
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => { setDetail(null); setDetailData(null); setSelOrders(new Set()); };

  const onReplaceFile = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f || !detail) return;
    if (f.size > maxBytes) { toast.error(`“${f.name}” exceeds the ${capLabel} upload limit`); return; }
    setReplacing(true);
    try {
      const dataUrl = await fileToDataUrl(f);
      const { data } = await api.put(`/designs/${detail.id}/image`, { image_data_url: dataUrl });
      const synced = Number(data?.synced_orders || 0);
      toast.success(
        synced > 0
          ? `Design image updated · refreshed on ${synced} linked order form${synced === 1 ? '' : 's'}`
          : 'Design image updated',
      );
      await refreshDetail(detail.id);
      load(query, page);
    } catch (err) {
      toast.error(extractError(err, 'Failed to update image'));
    } finally {
      setReplacing(false);
      if (replaceFileRef.current) replaceFileRef.current.value = '';
    }
  };

  const saveNote = async () => {
    if (!detail) return;
    setSavingNote(true);
    try {
      await api.put(`/designs/${detail.id}`, { note: noteDraft });
      toast.success('Note saved');
      await refreshDetail(detail.id);
      load(query, page);
    } catch (e) {
      toast.error(extractError(e, 'Failed to save note'));
    } finally {
      setSavingNote(false);
    }
  };

  // ----- Reassign -----
  const toggleOrder = (id) => {
    setSelOrders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllOrders = () => {
    const all = (detailData?.orders || []).map((o) => o.id);
    setSelOrders((prev) => (prev.size === all.length ? new Set() : new Set(all)));
  };

  const removeSelected = async () => {
    const ids = Array.from(selOrders);
    if (ids.length === 0) return;
    setReassignBusy(true);
    try {
      const { data } = await api.post('/designs/unassign', { order_ids: ids });
      toast.success(`${data.success_count} order${data.success_count === 1 ? '' : 's'} removed from ${detailData.design_no}.`);
      await refreshDetail(detail.id);
      load(query, page);
    } catch (e) {
      toast.error(extractError(e, 'Failed to remove orders'));
    } finally {
      setReassignBusy(false);
    }
  };

  const moveSelected = async () => {
    const ids = Array.from(selOrders);
    if (ids.length === 0 || !moveDesignId) return;
    setReassignBusy(true);
    try {
      const { data } = await api.post('/designs/assign-bulk', { order_ids: ids, design_id: moveDesignId });
      toast.success(`${data.success_count} order${data.success_count === 1 ? '' : 's'} moved to ${data.design_no}.`);
      setMoveOpen(false);
      setMoveDesignId(null);
      await refreshDetail(detail.id);
      load(query, page);
    } catch (e) {
      toast.error(extractError(e, 'Failed to move orders'));
    } finally {
      setReassignBusy(false);
    }
  };

  if (!enabled) {
    return (
      <div className="max-w-lg mx-auto mt-10 text-center bg-white/70 border border-[var(--divider)] rounded-xl p-8 shadow-sm" data-testid="design-master-disabled">
        <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center">
          <Palette size={26} className="text-indigo-600" strokeWidth={1.6} />
        </div>
        <h2 className="text-lg font-semibold text-slate-800 mb-1">Design Numbers are not enabled</h2>
        <p className="text-sm text-slate-600">
          The Design Master module is turned off for this company. A Super Admin can enable it under
          <span className="font-mono bg-slate-100 rounded px-1 mx-1">Admin → Companies</span>.
        </p>
      </div>
    );
  }

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);
  const allSelected = detailData && (detailData.orders || []).length > 0 && selOrders.size === (detailData.orders || []).length;

  return (
    <div className="space-y-4" data-testid="design-master-page">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search design number… (e.g. RD-101)"
            className="pl-8"
            data-testid="design-search-input"
          />
        </div>
        <div className="text-[12px] text-slate-500 tabular-nums" data-testid="design-total-count">
          {total} design{total === 1 ? '' : 's'}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => load(query, page)} data-testid="design-refresh-button" title="Refresh">
            <RefreshCw size={14} />
          </Button>
          {canManage && (
            <Button onClick={() => { setNewImage(''); setNewNote(''); setNewOpen(true); }} className="luxury-button" data-testid="design-new-button">
              <Plus size={15} className="mr-1.5" /> New Design
            </Button>
          )}
        </div>
      </div>

      {/* Grid — compact tiles keep the payload + render light. */}
      {loading ? (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2.5">
          {Array.from({ length: 16 }).map((_, i) => (
            <Card key={i} className="p-0 overflow-hidden">
              <Skeleton className="aspect-square w-full" />
              <div className="p-1.5 space-y-1.5"><Skeleton className="h-3 w-12" /></div>
            </Card>
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-16 text-slate-500" data-testid="design-empty-state">
          <Palette size={34} className="mx-auto mb-3 text-slate-300" />
          <p className="text-sm">No designs {query ? 'match your search.' : 'yet.'}</p>
          {canManage && !query && <p className="text-[12px] mt-1">Click <b>New Design</b> to create your first one (next: {nextPreview || 'RD-101'}).</p>}
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2.5">
          {list.map((d) => (
            <Card
              key={d.id}
              className="p-0 overflow-hidden cursor-pointer group hover:shadow-md transition-shadow duration-150"
              onClick={() => openDetail(d)}
              data-testid={`design-card-${d.design_no}`}
            >
              <div className="aspect-square w-full bg-slate-50 relative">
                {d.image_thumb ? (
                  <img src={d.image_thumb} alt={d.design_no} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-300">
                    <ImageOff size={22} />
                  </div>
                )}
                <span className="absolute top-1 left-1 inline-flex items-center gap-1 rounded bg-white/90 backdrop-blur px-1.5 py-0.5 text-[10px] font-mono font-semibold text-indigo-700 border border-indigo-200 shadow-sm">
                  {d.design_no}
                </span>
                {d.order_count > 0 && (
                  <span
                    className="absolute top-1 right-1 inline-flex items-center rounded bg-slate-900/70 backdrop-blur px-1 py-0.5 text-[9px] font-medium text-white shadow-sm"
                    data-testid={`design-card-usage-${d.design_no}`}
                    title={`Used in ${d.order_count} order${d.order_count === 1 ? '' : 's'}`}
                  >
                    {d.order_count}
                  </span>
                )}
              </div>
              <div className="p-1.5">
                {d.note ? (
                  <div className="text-[10px] text-slate-600 truncate mb-1" title={d.note} data-testid={`design-card-note-${d.design_no}`}>
                    {d.note}
                  </div>
                ) : null}
                {d.chips && d.chips.length > 0 ? (
                  <div className="flex flex-wrap gap-1" data-testid={`design-card-chips-${d.design_no}`}>
                    {d.chips.slice(0, 4).map((c) => (
                      <DesignChip key={`${c.link_type}-${c.label}`} chip={c} testId={`design-card-chip-${d.design_no}-${c.label}`} />
                    ))}
                    {d.chips.length > 4 && (
                      <span className="text-[9px] text-slate-400 self-center">+{d.chips.length - 4}</span>
                    )}
                  </div>
                ) : (
                  <div className="text-[10px] text-slate-400" data-testid={`design-card-nochips-${d.design_no}`}>No linked orders</div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination bar */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between pt-1" data-testid="design-pagination">
          <div className="text-[12px] text-slate-500 tabular-nums">
            Showing {rangeStart}–{rangeEnd} of {total}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => goToPage(page - 1)} disabled={page <= 1 || loading} data-testid="design-page-prev">
              <ChevronLeft size={14} className="mr-1" /> Prev
            </Button>
            <span className="text-[12px] text-slate-600 tabular-nums px-1">Page {page} / {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => goToPage(page + 1)} disabled={page >= totalPages || loading} data-testid="design-page-next">
              Next <ChevronRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* New Design dialog */}
      <Dialog open={newOpen} onOpenChange={(o) => { if (!creating) setNewOpen(o); }}>
        <DialogContent className="max-w-md" data-testid="design-new-dialog">
          <DialogHeader>
            <DialogTitle>New Design</DialogTitle>
            <DialogDescription>
              Upload one image for this design. It will be numbered <span className="font-mono font-semibold text-indigo-700">{nextPreview || 'RD-101'}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input ref={newFileRef} type="file" accept="image/*" hidden onChange={onNewFile} data-testid="design-new-file-input" />
            {newImage ? (
              <div className="relative border border-[var(--divider)] rounded-lg overflow-hidden bg-white">
                <img src={newImage} alt="Design preview" className="w-full max-h-72 object-contain bg-slate-50" data-testid="design-new-preview" />
                <button
                  type="button"
                  onClick={() => setNewImage('')}
                  className="absolute top-2 right-2 bg-white/90 text-rose-600 p-1 rounded-md border border-rose-200"
                  data-testid="design-new-clear"
                  title="Remove"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => newFileRef.current?.click()}
                className="w-full border-2 border-dashed border-[var(--divider)] rounded-lg py-10 flex flex-col items-center justify-center text-slate-500 hover:border-indigo-300 hover:text-indigo-600 transition-colors duration-150"
                data-testid="design-new-upload-zone"
              >
                <Upload size={22} className="mb-2" />
                <span className="text-sm font-medium">Choose an image</span>
                <span className="text-[11px] text-slate-400 mt-0.5">PNG / JPG up to {capLabel}</span>
              </button>
            )}
            <div>
              <label className="text-[12px] font-medium text-slate-700">Note / name <span className="text-slate-400 font-normal">(optional)</span></label>
              <Input
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="e.g. Bridal solitaire — client Meera"
                maxLength={200}
                className="mt-1"
                data-testid="design-new-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)} disabled={creating}>Cancel</Button>
            <Button onClick={createDesign} className="luxury-button" disabled={!newImage || creating} data-testid="design-new-submit">
              {creating ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Creating…</> : <>Create Design</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) closeDetail(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="design-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="font-mono text-indigo-700">{detailData?.design_no || detail?.design_no || 'Design'}</span>
              {detailData?.note && <span className="text-[13px] font-normal text-slate-500 truncate">— {detailData.note}</span>}
            </DialogTitle>
            <DialogDescription>
              {usesReference
                ? 'Chips are grouped by Page/Reference No. Click a chip to open its orders in a new tab.'
                : 'Chips are individual order forms. Click a chip to open the order in a new tab.'}
            </DialogDescription>
          </DialogHeader>
          {detailLoading || !detailData ? (
            <div className="space-y-3">
              <Skeleton className="w-full aspect-video" />
              <Skeleton className="h-6 w-40" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-[var(--divider)] bg-slate-50 overflow-hidden">
                {detailData.image ? (
                  <img src={detailData.image} alt={detailData.design_no} className="w-full max-h-[380px] object-contain" data-testid="design-detail-image" />
                ) : (
                  <div className="w-full aspect-video flex items-center justify-center text-slate-300"><ImageOff size={34} /></div>
                )}
              </div>

              {canManage && (
                <div className="flex items-center gap-2">
                  <input ref={replaceFileRef} type="file" accept="image/*" hidden onChange={onReplaceFile} data-testid="design-replace-file-input" />
                  <Button variant="outline" size="sm" onClick={() => replaceFileRef.current?.click()} disabled={replacing} data-testid="design-replace-button">
                    {replacing ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Updating…</> : <><RefreshCw size={14} className="mr-1.5" /> Replace Image</>}
                  </Button>
                  <span className="text-[11px] text-slate-400">PNG / JPG up to {capLabel}</span>
                </div>
              )}

              {/* Note editor */}
              {canManage && (
                <div>
                  <label className="text-[12px] font-medium text-slate-700">Note / name</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      placeholder="Add a short note so the team recognises this design"
                      maxLength={200}
                      data-testid="design-detail-note-input"
                    />
                    <Button
                      size="sm"
                      onClick={saveNote}
                      disabled={savingNote || (noteDraft || '') === (detailData.note || '')}
                      data-testid="design-detail-note-save"
                    >
                      {savingNote ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} className="mr-1" /> Save</>}
                    </Button>
                  </div>
                </div>
              )}

              {/* Chips */}
              <div>
                <div className="text-[12px] font-medium text-slate-700 mb-1.5">
                  Linked Orders {detailData.order_count > 0 && <span className="text-slate-400">({detailData.order_count})</span>}
                </div>
                {detailData.chips && detailData.chips.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5" data-testid="design-detail-chips">
                    {detailData.chips.map((c) => (
                      <DesignChip key={`${c.link_type}-${c.label}`} chip={c} testId={`design-detail-chip-${c.label}`} />
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-[12px] text-slate-500 bg-slate-50 border border-[var(--divider)] rounded-md px-3 py-2" data-testid="design-detail-nochips">
                    <Info size={13} /> No order forms are linked to this design yet.
                  </div>
                )}
              </div>

              {/* Reassign panel — select orders to remove or move to another design */}
              {canManage && (detailData.orders || []).length > 0 && (
                <div className="rounded-lg border border-[var(--divider)] bg-white" data-testid="design-reassign-panel">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--divider)]">
                    <div className="text-[12px] font-medium text-slate-700 flex items-center gap-1.5">
                      <ArrowRightLeft size={13} className="text-slate-500" /> Manage / reassign orders
                    </div>
                    <button type="button" onClick={selectAllOrders} className="text-[11px] text-indigo-600 hover:underline" data-testid="design-reassign-select-all">
                      {allSelected ? 'Clear' : 'Select all'}
                    </button>
                  </div>
                  <div className="max-h-52 overflow-y-auto divide-y divide-[var(--divider)]">
                    {detailData.orders.map((o) => {
                      const sel = selOrders.has(o.id);
                      return (
                        <label
                          key={o.id}
                          className={`flex items-center gap-2 px-3 py-1.5 text-[12px] cursor-pointer transition-colors duration-150 ${sel ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}
                          data-testid={`design-reassign-order-${o.order_no}`}
                        >
                          <span className={`w-4 h-4 rounded border flex items-center justify-center ${sel ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-slate-300 bg-white'}`}>
                            {sel && <Check size={11} />}
                          </span>
                          <input type="checkbox" className="sr-only" checked={sel} onChange={() => toggleOrder(o.id)} />
                          <span className={`w-1.5 h-1.5 rounded-full ${o.active ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                          <span className="font-mono text-slate-700">{o.order_no}</span>
                          {o.reference_no != null && <span className="text-slate-400">· Page {o.reference_no}</span>}
                          <span className="ml-auto text-[11px] text-slate-400">{o.stage || ''}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 border-t border-[var(--divider)] bg-slate-50/60">
                    <span className="text-[11px] text-slate-500">{selOrders.size} selected</span>
                    <div className="ml-auto flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={removeSelected} disabled={selOrders.size === 0 || reassignBusy} data-testid="design-reassign-remove">
                        {reassignBusy ? <Loader2 size={13} className="animate-spin" /> : <><Unlink size={13} className="mr-1" /> Remove from design</>}
                      </Button>
                      <Button size="sm" className="luxury-button" onClick={() => { setMoveDesignId(null); setMoveOpen(true); }} disabled={selOrders.size === 0 || reassignBusy} data-testid="design-reassign-move">
                        <ArrowRightLeft size={13} className="mr-1" /> Move to…
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="text-[11px] text-slate-400 flex items-center gap-2 pt-1 border-t border-[var(--divider)]">
                <span>Created {detailData.created_at ? formatDate(detailData.created_at) : '—'}</span>
                {detailData.created_by_email && <span>· by {detailData.created_by_email}</span>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeDetail}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move-to-design dialog (reassign target picker) */}
      <Dialog open={moveOpen} onOpenChange={(o) => { if (!reassignBusy) setMoveOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="design-move-dialog">
          <DialogHeader>
            <DialogTitle>Move {selOrders.size} order{selOrders.size === 1 ? '' : 's'} to another design</DialogTitle>
            <DialogDescription>Pick the target design. The selected orders will be re-linked — no image is uploaded or duplicated.</DialogDescription>
          </DialogHeader>
          <DesignPicker companyId={companyId} value={moveDesignId} onChange={(id) => setMoveDesignId(id)} testIdPrefix="design-move-picker" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveOpen(false)} disabled={reassignBusy}>Cancel</Button>
            <Button className="luxury-button" onClick={moveSelected} disabled={!moveDesignId || reassignBusy} data-testid="design-move-confirm">
              {reassignBusy && <Loader2 size={14} className="mr-1.5 animate-spin" />}
              Move Orders
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
